import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import useInfiniteScroll, {
  ScrollDirection,
} from "react-easy-infinite-scroll-hook";
import { ActionCableContext } from "../../../../../../App";
import { filterByUniqueKey } from "../../../../../../utils";
import {
  sendSMS,
  translateMsg,
} from "../../../../../../utils/api/communication.api";
import { markMessageAsRead } from "../../../../../../utils/api/header-notifications.api";
import { getThreadMessages } from "../../../../../../utils/api/history.api";
import { uploadSmsImage } from "../../../../../../utils/api/lead.api";
import { useBreadcrumb } from "../../../../../../utils/context/BreadCrumsContext/BreadcrumbContext";
import { useTwilioState } from "../../../../../../utils/context/TwilioContext";
import { useUserInfo } from "../../../../../../utils/hooks/useUserInfo";
import { loadOutboundLine } from "../../../../../../pages/(dashboard-routes)/bdc-campaign/CreateCampaign/utils";
import { getOutbooundLine } from "../../../../../../utils/api/bdc-campaign.api";

type useMessageThreadTypes = {
  clickedRecord: any;
  clickedRow: any;
  type: string;
  activeIndex?: number;
};

export const useMessageThread = ({
  clickedRecord,
  clickedRow,
  type,
  activeIndex,
}: useMessageThreadTypes) => {
  const [thread, setMessageThread] = useState<any>({});
  const [initialLoad, setInitialLoad] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [device, setDevice] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [mentions, setMentions] = useState<any>([]);
  const [translate, setTranslate] = useState<any>({});
  const [translatedArray, setTranslatedArray] = useState<any>([]);
  const [loading, setLoading] = useState(true); // Track loading state
  const { setDisableOnErrorRes } = useBreadcrumb();
  const perPage = 200;
  const { cable } = useContext(ActionCableContext);
  const [lineDevices, setLineDevices] = useState<any>([]);

  const subscriptionRef = useRef<any>(null); // Use ref to store the subscription

  const user = useUserInfo("user");
  const dealership = user?.dealership;

  const toast = useToast();

  const mutation = useMutation<any, Error, any, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await sendSMS(payload, type);
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        description: `${
          mentions?.length ? "Ping" : "Message"
        } Sent Successfully`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setMentions([]);
    },
    onError: (error: any) => {
      if (error?.response?.data?.errors === "The Contact is DNC") {
        setDisableOnErrorRes(true);
      }

      toast({
        description: `Error Sending ${mentions?.length ? "Ping" : "Message"}: ${
          error?.response?.data?.errors?.toString() ??
          error?.response?.data?.error ??
          "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const mutationTranslate = useMutation<any, Error, any, { payload: any }>({
    mutationFn: async (payload) => {
      try {
        const response = await translateMsg(payload);
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onMutate: (payload) => {
      return { payload };
    },
    onSuccess: (data, variables, context) => {
      setTranslatedArray((prev: any) => [
        ...prev,
        {
          id: context?.payload.id,
          translated: data?.data?.translated_text,
        },
      ]);
    },
    onError: (error: any) => {
      toast({
        description: `Error Translating ${
          mentions?.length ? "Ping" : "Message"
        }: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });
  const translateMessage = async (payload: any) => {
    try {
      await mutationTranslate.mutateAsync({
        id: payload?.id,
        payload: {
          text: payload?.content,
        },
      });
    } catch (error) {}
  };

  const attachmentMutation = useMutation<any, Error, any, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await uploadSmsImage({ payload });
        setAttachments((value) => [...value, response.data]);
        return response.data.url;
      } catch (error) {
        throw error;
      }
    },
  });

  const { created_at: selectedDate } = clickedRecord || {};
  const { id: contactId } = clickedRow || {};

  const { messages = [] } = thread || {};

  const divRefCurrent = useRef<HTMLDivElement>(null);

  const sendMessage = async () => {
    try {
      await mutation.mutateAsync({
        to: clickedRow?.phone_number,
        from: device,
        contact_id: type == "message" ? clickedRow?.id : clickedRow?.uuid,
        content: messageBody,
        dealership_id:
          type == "message" ? user.dealership.id : clickedRow?.dealership_uuid,
        user_id: user.id,
        media_urls: attachments.map((attachment: any) => attachment.url),
        user_ids: mentions.filter(function (item: number, pos: number) {
          return mentions.indexOf(item) === pos;
        }),
        ping: !!mentions.length,
      });
    } catch (error) {}

    setAttachments([]);
    setMessageBody("");
  };

  const messageMutation = useMutation(markMessageAsRead);

  useEffect(() => {
    try {
      if (!cable) return;
      const subscription = cable.subscriptions.create(
        {
          channel: "MessageChannel",
          contact_id: clickedRow.id,
          dealership_id: dealership.id,
        },
        {
          connected: () => {
            console.log("message connected");
          },
          disconnected: () => {
            console.log("disconnected");
          },
          received: async (data: any) => {
            if (!document.getElementById("message-container")) return;
            console.log("subscriptionRef.current", subscriptionRef.current);
            // Handle incoming data from Action Cable
            const messagePayload = JSON.parse(data?.message);

            // if (messagePayload.message_direction !== "outbound") {
            // setMessageThread({});
            // setAttachments([]);
            // setMessageBody("");
            setMessageThread((prevThread: any) => {
              // Check if the message already exists
              const messageExists = prevThread.messages.some(
                (msg: any) => msg.id === messagePayload.id
              );

              // Only add the new message if it's not already in the list
              return messageExists
                ? prevThread
                : {
                    ...prevThread,
                    messages: [...prevThread.messages, messagePayload],
                  };
            });

            // messageMutation.mutate({
            //   dId: dealership?.id,
            //   id: messagePayload?.id,
            // });

            // await setThreadInfo(moment().toISOString(), 1, false, true);
          },
          // },
        }
      );

      // Store the subscription in the ref
      subscriptionRef.current = subscription;
    } catch (error) {}

    // Cleanup the subscription when the component unmounts
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null; // Mark unmounted
        console.log("Unsubscribed from ActionCable on unmount");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cable, clickedRow, dealership]);

  const handleUploadDocuments = async (e: any) => {
    if (e.target.files) {
      const file = e.target.files[0];
      if (file.size / 1024 / 1024 > 20) {
        return toast({
          description: "Only files less than 20mb are acceptable.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("dealership_id", dealership.id);
      e.target.value = "";

      try {
        // You can write the URL of your server or any other endpoint used for file upload
        attachmentMutation.mutate(formData);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const dateFormatter = (date: string) => {
    return moment(date).format("MM-DD-YYYY");
  };

  const arrayFormatter = (arr: any, value: number) => {
    if (Array.isArray(arr)) {
      const transformedArr = [...arr, value];
      return transformedArr;
    }
    return [value];
  };

  const setLoadedThreadInfo = (
    currentDate: string,
    messagesThread: any,
    prev: any
  ) => {
    const transformedMessageThread = threadPagination(messagesThread);
    return {
      ...prev,
      [dateFormatter(currentDate)]: {
        ...transformedMessageThread,
        pagination: {
          ...transformedMessageThread.pagination,
          current_page: arrayFormatter(
            prev[dateFormatter(currentDate)]?.pagination?.current_page,
            transformedMessageThread.pagination.current_page
          ),
        },
      },
    };
  };

  const threadPagination = (messageThread: any) => {
    const { messages, ...paginationInfo } = messageThread;
    return paginationInfo;
  };

  const shouldFetchMessages = (date: string, page: number) => {
    const { loadedThreads } = thread || {};
    const { current_page } =
      loadedThreads[dateFormatter(date)]?.pagination || {};
    return !current_page?.includes(page);
  };

  const setThreadInfo = async (
    date: string,
    page: number,
    next = true,
    reset = false
  ) => {
    // setCounter(prev=>prev+1)
    const messagesThread = await generateMessageThread(
      date,
      contactId,
      page,
      perPage
    );

    const updatedLoadedThreads = setLoadedThreadInfo(
      date,
      messagesThread,
      thread.loadedThreads || {}
    );

    const threadOfMessages = {
      loadedThreads: updatedLoadedThreads,
      ...messagesThread,
      messages: reset
        ? [...messagesThread.messages].reverse()
        : next
        ? [
            ...(thread.messages || []),
            ...[...(messagesThread.messages || [])].reverse(),
          ]
        : [
            ...[...(messagesThread.messages || [])].reverse(),
            ...(thread.messages || []),
          ],
    };

    setMessageThread(threadOfMessages);

    return messagesThread;
  };

  const getLoadedThread = (date: string) => {
    const { loadedThreads } = thread || {};
    return loadedThreads[dateFormatter(date)];
  };

  const getMessagesOfDate = (date: string) => {
    return messages.filter(
      (message: any) =>
        dateFormatter(message.created_at) === dateFormatter(date)
    ).length;
  };

  const next = async (direction: ScrollDirection) => {
    console.log("next....................................");
    if (!messages.length || !document.getElementById("message-container"))
      return;
    if (!initialLoad) {
      const rect = divRefCurrent?.current?.getBoundingClientRect();
      const { top = 0, bottom = 0 } = rect || {};
      const isInView = top < window.innerHeight && bottom >= 0;
      if (!isInView) {
        divRefCurrent.current?.scrollIntoView({
          behavior: "smooth",
        });
      }
      setInitialLoad(true);
      return;
    }

    if (direction === "up") {
      const currentDate = messages?.[0]?.created_at;
      const { pagination, prev_date } = getLoadedThread(currentDate) || {};
      const { total_pages, current_page } = pagination || {};
      const currentPage = current_page?.[current_page?.length - 1];
      const { messages_count, date } = prev_date || {};
      if (total_pages <= currentPage && !messages_count) return;
      if (total_pages <= currentPage && messages_count) {
        const formattedDate = date;
        if (shouldFetchMessages(formattedDate, 1) || type == "guest-message") {
          await setThreadInfo(formattedDate, 1, false);
        }

        return;
      }
      if (
        shouldFetchMessages(currentDate, currentPage + 1) ||
        type == "guest-message"
      ) {
        return await setThreadInfo(currentDate, currentPage + 1, false);
      }
    } else {
      const currentDate = messages?.[messages.length - 1]?.created_at;
      const { pagination, next_date } = getLoadedThread(currentDate) || {};
      const { current_page, total_count } = pagination || {};
      const currentPage = current_page?.[0];
      const { messages_count, date } = next_date || {};
      const messagesLength = getMessagesOfDate(currentDate);
      if (
        (currentPage <= 1 || messagesLength >= total_count) &&
        !messages_count
      )
        return;
      if (
        (currentPage <= 1 || messagesLength >= total_count) &&
        messages_count
      ) {
        const lastPage = Math.ceil(messages_count / perPage);
        const formattedDate = date;
        if (shouldFetchMessages(formattedDate, lastPage))
          await setThreadInfo(formattedDate, lastPage);
        return;
      }
      if (shouldFetchMessages(currentDate, currentPage - 1))
        await setThreadInfo(currentDate, currentPage - 1);
    }
  };

  const ref = useInfiniteScroll({
    next: (direction) =>
      document.getElementById("message-container")
        ? next(direction)
        : (null as any),
    rowCount: messages.length,
    hasMore: { down: true, up: true },
  });

  const generateMessageThread = async (
    selectedDate: string,
    contactId: string,
    page = 1,
    perPage = 100000
  ) => {
    setLoading(true);
    const res = await getThreadMessages(
      {
        date: selectedDate,
        contactId: type == "message" ? contactId : clickedRow?.uuid,
        page: page,
        perPage: perPage,
        dealershipId:
          type == "message" ? dealership.id : clickedRow?.dealership_uuid,
      },
      type
    );
    const { data: messagesThread } = res || {};
    setLoading(false);
    return messagesThread;
  };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    if (!contactId) return;

    if (
      !(
        (activeIndex === 2 && clickedRecord) ||
        clickedRecord?.record_type === "message"
      )
    )
      return;

    try {
      let allMessages: any[] = [];
      let loadedThreadsCopy = {};
      let dateToLoad = selectedDate;

      const messagesThread: any = await generateMessageThread(
        dateToLoad,
        contactId
      );

      // Reverse the initial day's messages to ensure reverse chronological order
      allMessages = [...messagesThread.messages].reverse();
      loadedThreadsCopy = setLoadedThreadInfo(dateToLoad, messagesThread, {});

      // Load previous days' messages if available
      let prevDateInfo = messagesThread?.prev_date;
      for (let i = 0; i < 5 && prevDateInfo?.messages_count; i++) {
        const prevDate = prevDateInfo.date;
        const lastPage = Math.ceil(prevDateInfo.messages_count / perPage);

        const prevMessagesThread: any = await generateMessageThread(
          prevDate,
          contactId,
          lastPage
        );

        // Reverse the previous day's messages to ensure reverse chronological order
        const prevMessagesReversed = [...prevMessagesThread.messages].reverse();

        // Prepend the reversed previous day's messages
        allMessages = [...prevMessagesReversed, ...allMessages];

        loadedThreadsCopy = setLoadedThreadInfo(
          prevDate,
          prevMessagesThread,
          loadedThreadsCopy
        );

        // Continue to look for the next previous day
        prevDateInfo = prevMessagesThread?.prev_date;
      }

      setMessageThread((prev: any) => ({
        ...prev,
        ...messagesThread,
        loadedThreads: loadedThreadsCopy,
        messages: allMessages,
      }));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }

    const response = await getOutbooundLine({
      dealership_id: dealership.id,
      q: "",
    });

    const lineDevices = response?.data?.map((leadSource: any) => ({
      value: leadSource.phone_number,
      label: `${leadSource.title} (${leadSource.phone_number})`,
      default: leadSource.default,
    })) ?? [];

    setLineDevices(lineDevices);
    const defaultLine =
      lineDevices?.find((line: any) => line.default)?.value ||
      lineDevices?.[0]?.value;
    setDevice(defaultLine || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // Ensure scroll happens after DOM update
    setTimeout(() => {
      divRefCurrent.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 0);
  }, [activeIndex, clickedRecord, contactId]);

  useEffect(() => {
    if (!contactId) return;
    if (
      !(
        (activeIndex === 2 && clickedRecord) ||
        clickedRecord?.record_type === "message"
      )
    )
      return;
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, clickedRecord, contactId]);

  return {
    ref,
    thread,
    handleUploadDocuments,
    sendMessage,
    lineDevices,
    attachmentMutation,
    dateFormatter,
    divRefCurrent,
    device,
    setDevice,
    attachments,
    setAttachments,
    messageBody,
    setMessageBody,
    mutation,
    next,
    setMessageThread,
    setInitialLoad,
    mentions,
    setMentions,
    translate,
    setTranslate,
    translateMessage,
    translatedArray,
    setTranslatedArray,
    loading,
  };
};
